import { buildLoggerStreamUrl } from '@app/modules/logger/screens/stream/helpers/build-logger-stream-url';
import { AuditLiveMention, toTimestamp } from '@mi-front/core';
import { Chip, BtnLink, MrtTable } from '@mi-front/ui';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { Alert } from '@mui/material';
import { isToday, parseISO } from 'date-fns';
import { useIntl } from 'react-intl';
import { LDate } from '../../../../../../components/date/LDate';
import { LTime } from '../../../../../../components/date/LTime';
import { ListingResultProps } from '../../../../../../core/reports/components/ListingView';
import { useReportCtx } from '../../../../../../core/reports/report-ctx';
import { useActiveStation } from '../../../../../../core/stations/hooks/useActiveStation';
import { LiveMentionConfidence } from '../../../../components/live-mentions/LiveMentionConfidence';
import MentionConfirmedBySchedule from './MentionConfirmedBySchedule';
import { LiveMentionsMatchesListing } from './types';

import { FormattedMessage } from 'react-intl';
export const LiveMentionsList = (p: ListingResultProps<LiveMentionsMatchesListing>) => {
  const { values } = useReportCtx<LiveMentionsMatchesListing>();
  const { formatMessage } = useIntl();
  const $t = formatMessage;
  const station = useActiveStation();

  const showProcessEndDateMsg = isToday(parseISO(values.from)) && isToday(parseISO(values.to));

  const showOnStreamUrlFn = (match: AuditLiveMention) =>
    buildLoggerStreamUrl(station.slug, {
      cursor: toTimestamp(match.datetime),
      tab: 'livementions',
    });

  if (showProcessEndDateMsg) {
    return (
      <Alert severity={'warning'}>
        <FormattedMessage
          id="audit.liveMentions.processingMessage"
          defaultMessage="Las menciones en vivo se están procesando. Los resultados estarán disponibles en breve."
        />
      </Alert>
    );
  }

  return (
    <MrtTable
      {...p.mrtProps}
      columns={[
        {
          accessorKey: 'datetime',
          header: $t({
            id: 'audit.props.time',
            defaultMessage: 'Hora',
          }),
          size: 160,
          Cell: ({ row }) => (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <LDate value={row.original.datetime} day="2-digit" month="2-digit" year="2-digit" />
              <LTime value={row.original.datetime} second={'numeric'} />
            </div>
          ),
        },
        {
          header: $t({
            id: 'audit.props.type',
            defaultMessage: 'Tipo',
          }),
          size: 90,
          Cell: ({ row }) => (
            <Chip type="primary" size="sm">
              {$t({
                id: 'audit.liveMention.badge',
                defaultMessage: 'Live Mention',
              })}
            </Chip>
          ),
        },
        {
          header: $t({
            id: 'common.text.name',
            defaultMessage: 'Nombre',
          }),
          accessorKey: 'name',
          enableColumnFilter: false,
          size: 180,
          Cell: ({ row: { original: mention } }) => (
            <div>
              {mention.name}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {mention.confirmedSchedule && (
                  <MentionConfirmedBySchedule
                    match={mention}
                    schedule={mention.confirmedSchedule}
                  />
                )}
              </div>
            </div>
          ),
        },
        {
          header: $t({
            id: 'common.text.description',
            defaultMessage: 'Descripción',
          }),
          size: 220,
          accessorKey: 'description',
        },
        {
          header: $t({
            id: 'common.status',
            defaultMessage: 'Estado',
          }),
          size: 120,
          accessorKey: 'confidence',
          Cell: ({ row }) => <LiveMentionConfidence value={row.original.confidence} />,
        },
        {
          header: $t({
            id: 'common.text.actions',
            defaultMessage: 'Acciones',
          }),
          Cell: ({ row }) => (
            <BtnLink
              size="xs"
              to={showOnStreamUrlFn(row.original)}
              openInNewTab
              icon={<OpenInNewIcon />}
              className="mr-2"
            >
              <FormattedMessage
                id="audit.matchesScreen.showOnStreamBtn"
                defaultMessage="Mostrar en stream"
              />
            </BtnLink>
          ),
        },
      ]}
    />
  );
};
